import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Infinity as InfinityIcon, MessageSquare, Mic, Paperclip, Plus, SendHorizontal, X, } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState, } from 'react';
import { ApiError } from '@/shared/api';
import { uiCopy } from '@/shared/copy';
import { cn } from '@/shared/lib/cn';
import { advanceEngagementAction } from '@/features/advance-engagement';
import { deferPlacementAction, movePlacementAction, recordContactAction, } from '@/features/record-contact';
import { fetchProactiveAgentChatMessages, postAgentChatMessage, } from '../api/agent-chat-api';
import { AGENT_CHAT_ERROR_FALLBACK, PENDING_AGENT_REPLY, addChatTab, appendProactiveAssistantMessage, clampChatWidth, closeChatTab, loadChatStore, loadChatWidth, saveChatStore, saveChatWidth, selectChatTab, sendUserMessage, setAssistantMessageContent, setMessageActionState, validateMdAttachment, AGENT_CHAT_WIDTH_MAX, AGENT_CHAT_WIDTH_MIN, } from '../model/chats';
import { MarkdownView } from './markdown-view';
const PROACTIVE_POLL_MS = 8_000;
async function runChatAction(action) {
    switch (action.command) {
        case 'RecordContact': {
            if (!action.payload.placementId) {
                throw new Error('Нет placementId');
            }
            await recordContactAction(action.payload.placementId, action.payload.note);
            return;
        }
        case 'DeferPlacement': {
            if (!action.payload.placementId) {
                throw new Error('Нет placementId');
            }
            await deferPlacementAction(action.payload.placementId, action.payload.note);
            return;
        }
        case 'MovePlacement': {
            if (!action.payload.placementId || !action.payload.targetQueueId) {
                throw new Error('Нет placementId или targetQueueId');
            }
            await movePlacementAction(action.payload.placementId, action.payload.targetQueueId);
            return;
        }
        case 'AdvanceEngagement': {
            if (!action.payload.engagementId) {
                throw new Error('Нет engagementId');
            }
            await advanceEngagementAction(action.payload.engagementId, action.payload.note);
            return;
        }
        default:
            throw new Error('Неизвестная команда');
    }
}
function readFileAsText(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            resolve(typeof reader.result === 'string' ? reader.result : '');
        };
        reader.onerror = () => {
            reject(reader.error ?? new Error('read failed'));
        };
        reader.readAsText(file);
    });
}
export function AgentChatAside({ onWidthChange, } = {}) {
    const fileInputId = useId();
    const fileInputRef = useRef(null);
    const asideRef = useRef(null);
    const listRef = useRef(null);
    const resizeRef = useRef(null);
    const [store, setStore] = useState(() => loadChatStore());
    const [width, setWidth] = useState(() => loadChatWidth());
    const [resizing, setResizing] = useState(false);
    const [draft, setDraft] = useState('');
    const [attachment, setAttachment] = useState(null);
    const [attachError, setAttachError] = useState(null);
    const [sendError, setSendError] = useState(null);
    const [sending, setSending] = useState(false);
    const [modelLabel, setModelLabel] = useState(null);
    useEffect(() => {
        saveChatStore(store);
    }, [store]);
    useEffect(() => {
        saveChatWidth(width);
        onWidthChange?.(width);
    }, [onWidthChange, width]);
    useEffect(() => {
        const el = listRef.current;
        if (!el)
            return;
        el.scrollTop = el.scrollHeight;
    }, [store.activeTabId, store.tabs]);
    useEffect(() => {
        const aside = asideRef.current;
        if (!aside)
            return;
        const onWheel = (event) => {
            // Keep native horizontal scroll on the tab strip.
            if (Math.abs(event.deltaX) >= Math.abs(event.deltaY))
                return;
            const scroller = listRef.current;
            if (!scroller) {
                event.preventDefault();
                return;
            }
            const { scrollTop, scrollHeight, clientHeight } = scroller;
            const maxScroll = Math.max(0, scrollHeight - clientHeight);
            const atTop = scrollTop <= 0;
            const atBottom = scrollTop >= maxScroll - 1;
            const overScroller = scroller.contains(event.target);
            if (overScroller) {
                if (maxScroll <= 0 || (event.deltaY < 0 && atTop) || (event.deltaY > 0 && atBottom)) {
                    event.preventDefault();
                }
                return;
            }
            // Wheel over composer / chrome: move the message list, never the page.
            event.preventDefault();
            if (maxScroll > 0) {
                scroller.scrollTop = Math.min(maxScroll, Math.max(0, scrollTop + event.deltaY));
            }
        };
        aside.addEventListener('wheel', onWheel, { passive: false });
        return () => {
            aside.removeEventListener('wheel', onWheel);
        };
    }, []);
    useEffect(() => {
        let cancelled = false;
        const pull = async () => {
            try {
                const response = await fetchProactiveAgentChatMessages();
                if (cancelled || response.items.length === 0)
                    return;
                setStore((prev) => {
                    let next = prev;
                    for (const item of response.items) {
                        next = appendProactiveAssistantMessage(next, {
                            proactiveId: item.id,
                            content: item.contentMarkdown,
                            actions: Array.isArray(item.actions) ? item.actions : [],
                        });
                    }
                    return next;
                });
            }
            catch {
                /* poll quietly — chat must stay usable */
            }
        };
        void pull();
        const timer = window.setInterval(() => {
            void pull();
        }, PROACTIVE_POLL_MS);
        return () => {
            cancelled = true;
            window.clearInterval(timer);
        };
    }, []);
    useEffect(() => {
        if (!resizing)
            return;
        const onPointerMove = (event) => {
            const start = resizeRef.current;
            if (!start)
                return;
            const next = clampChatWidth(start.startWidth + (start.startX - event.clientX));
            setWidth(next);
        };
        const onPointerUp = () => {
            resizeRef.current = null;
            setResizing(false);
            document.body.style.removeProperty('cursor');
            document.body.style.removeProperty('user-select');
        };
        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', onPointerUp);
        window.addEventListener('pointercancel', onPointerUp);
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        return () => {
            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerup', onPointerUp);
            window.removeEventListener('pointercancel', onPointerUp);
            document.body.style.removeProperty('cursor');
            document.body.style.removeProperty('user-select');
        };
    }, [resizing]);
    const activeTab = store.tabs.find((tab) => tab.id === store.activeTabId) ?? store.tabs[0];
    const canSend = !sending && (draft.trim().length > 0 || attachment != null);
    const updateStore = useCallback((next) => {
        setStore(next);
    }, []);
    const handleSend = useCallback(() => {
        if (!canSend)
            return;
        const content = draft;
        const file = attachment ?? undefined;
        const tabId = store.activeTabId;
        const nextStore = sendUserMessage(store, {
            content,
            attachment: file,
            assistantContent: PENDING_AGENT_REPLY,
        });
        const active = nextStore.tabs.find((tab) => tab.id === nextStore.activeTabId) ??
            nextStore.tabs[0];
        const assistantMessage = [...active.messages]
            .reverse()
            .find((message) => message.role === 'assistant');
        if (!assistantMessage)
            return;
        updateStore(nextStore);
        setDraft('');
        setAttachment(null);
        setAttachError(null);
        setSendError(null);
        setSending(true);
        if (fileInputRef.current)
            fileInputRef.current.value = '';
        void postAgentChatMessage({
            tabId,
            content,
            attachment: file,
        })
            .then((response) => {
            setModelLabel(response.model);
            setStore((prev) => setAssistantMessageContent(prev, {
                messageId: assistantMessage.id,
                content: response.text,
            }));
        })
            .catch((err) => {
            const message = err instanceof ApiError
                ? err.message
                : err instanceof Error
                    ? err.message
                    : AGENT_CHAT_ERROR_FALLBACK;
            setSendError(message || uiCopy.agentChatErrorFallback);
            setStore((prev) => setAssistantMessageContent(prev, {
                messageId: assistantMessage.id,
                content: message || uiCopy.agentChatErrorFallback,
            }));
        })
            .finally(() => {
            setSending(false);
        });
    }, [attachment, canSend, draft, store, updateStore]);
    const handleActionClick = useCallback((messageId, action) => {
        setStore((prev) => setMessageActionState(prev, {
            messageId,
            actionId: action.id,
            state: 'pending',
            error: null,
        }));
        void runChatAction(action)
            .then(() => {
            setStore((prev) => setMessageActionState(prev, {
                messageId,
                actionId: action.id,
                state: 'done',
                error: null,
            }));
        })
            .catch((err) => {
            const message = err instanceof ApiError
                ? err.message
                : err instanceof Error
                    ? err.message
                    : 'Не удалось выполнить действие';
            setStore((prev) => setMessageActionState(prev, {
                messageId,
                actionId: action.id,
                state: 'error',
                error: message,
            }));
        });
    }, []);
    const onSubmit = (event) => {
        event.preventDefault();
        handleSend();
    };
    const onKeyDown = (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleSend();
        }
    };
    const onPickFile = async (event) => {
        const file = event.target.files?.[0];
        if (!file)
            return;
        try {
            const content = await readFileAsText(file);
            const result = validateMdAttachment({
                fileName: file.name,
                content,
                byteLength: file.size,
            });
            if (!result.ok) {
                setAttachment(null);
                setAttachError(result.error);
                return;
            }
            setAttachment(result.attachment);
            setAttachError(null);
        }
        catch {
            setAttachment(null);
            setAttachError('Не получилось прочитать файл. Попробуйте ещё раз.');
        }
    };
    const onResizePointerDown = (event) => {
        event.preventDefault();
        resizeRef.current = { startX: event.clientX, startWidth: width };
        setResizing(true);
    };
    const onResizeKeyDown = (event) => {
        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            setWidth((prev) => clampChatWidth(prev + 16));
        }
        else if (event.key === 'ArrowRight') {
            event.preventDefault();
            setWidth((prev) => clampChatWidth(prev - 16));
        }
        else if (event.key === 'Home') {
            event.preventDefault();
            setWidth(AGENT_CHAT_WIDTH_MAX);
        }
        else if (event.key === 'End') {
            event.preventDefault();
            setWidth(AGENT_CHAT_WIDTH_MIN);
        }
    };
    const modelCaption = sending
        ? uiCopy.agentChatModelPending
        : modelLabel ?? uiCopy.agentChatModelOffline;
    return (_jsxs("aside", { ref: asideRef, className: "relative flex h-full max-h-[100vh] w-full flex-col overflow-hidden overscroll-contain border-border bg-card xl:w-[var(--agent-chat-width)] xl:shrink-0 xl:border-l", style: {
            ['--agent-chat-width']: `${width}px`,
        }, "aria-label": uiCopy.agentChatTitle, children: [_jsx("div", { role: "separator", "aria-orientation": "vertical", "aria-label": uiCopy.agentChatResize, "aria-valuemin": AGENT_CHAT_WIDTH_MIN, "aria-valuemax": AGENT_CHAT_WIDTH_MAX, "aria-valuenow": width, tabIndex: 0, onPointerDown: onResizePointerDown, onKeyDown: onResizeKeyDown, className: cn('absolute inset-y-0 left-0 z-10 hidden w-3 -translate-x-1/2 cursor-col-resize touch-none xl:block', 'before:absolute before:inset-y-0 before:left-1/2 before:w-px before:-translate-x-1/2 before:bg-border', resizing && 'before:bg-primary') }), _jsxs("div", { className: "flex h-10 shrink-0 items-stretch border-b border-border bg-muted/40", children: [_jsx("h2", { className: "sr-only", children: uiCopy.agentChatTitle }), _jsx("div", { className: "scrollbar-none flex min-w-0 flex-1 items-stretch overflow-x-auto overscroll-contain", role: "tablist", "aria-label": uiCopy.agentChatTitle, children: store.tabs.map((tab, index) => {
                            const isActive = tab.id === store.activeTabId;
                            return (_jsxs("div", { role: "presentation", className: cn('group relative flex max-w-[11rem] min-w-[7rem] shrink-0 items-stretch', isActive
                                    ? 'z-[1] bg-background text-foreground shadow-[inset_0_-1px_0_0_var(--background)]'
                                    : 'bg-transparent text-muted-foreground hover:bg-muted/70', !isActive &&
                                    index > 0 &&
                                    store.tabs[index - 1]?.id !== store.activeTabId
                                    ? 'before:absolute before:inset-y-2 before:left-0 before:w-px before:bg-border'
                                    : null, isActive
                                    ? 'border-x border-t border-border -mb-px'
                                    : null), children: [_jsxs("button", { type: "button", role: "tab", "aria-selected": isActive, className: "flex min-w-0 flex-1 items-center gap-1.5 px-2.5 text-left text-xs font-medium", onClick: () => updateStore(selectChatTab(store, tab.id)), children: [_jsx(MessageSquare, { className: "h-3.5 w-3.5 shrink-0 opacity-70", "aria-hidden": true }), _jsx("span", { className: "truncate", children: tab.title })] }), _jsx("button", { type: "button", className: cn('mr-1 inline-flex h-7 w-7 shrink-0 items-center justify-center self-center rounded-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground', isActive
                                            ? 'opacity-100'
                                            : 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100'), "aria-label": uiCopy.agentChatCloseTab, onClick: (event) => {
                                            event.stopPropagation();
                                            updateStore(closeChatTab(store, tab.id));
                                        }, children: _jsx(X, { className: "h-3.5 w-3.5", "aria-hidden": true }) })] }, tab.id));
                        }) }), _jsx("button", { type: "button", className: "inline-flex h-10 w-10 shrink-0 items-center justify-center border-l border-border text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground", "aria-label": uiCopy.agentChatNewTab, onClick: () => updateStore(addChatTab(store)), children: _jsx(Plus, { className: "h-4 w-4", "aria-hidden": true }) })] }), _jsx("div", { ref: listRef, className: "scrollbar-none min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain px-3 py-3", children: activeTab.messages.length === 0 ? (_jsx("p", { className: "text-sm text-muted-foreground", children: uiCopy.agentChatEmpty })) : (activeTab.messages.map((message) => (_jsxs("article", { className: cn('rounded-md border border-border px-3 py-2', message.role === 'user' ? 'bg-background' : 'bg-muted/40'), children: [_jsxs("p", { className: "mb-1 flex flex-wrap items-center gap-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground", children: [_jsx("span", { children: message.role === 'user'
                                        ? uiCopy.agentChatUserLabel
                                        : uiCopy.agentChatAssistantLabel }), message.source === 'proactive' ? (_jsx("span", { className: "rounded-sm border border-border bg-background px-1.5 py-0.5 text-[10px] font-semibold normal-case tracking-normal text-foreground", children: uiCopy.agentChatProactiveBadge })) : null] }), message.content ? (_jsx(MarkdownView, { content: message.content })) : message.role === 'assistant' ? (_jsx("p", { className: "text-sm text-muted-foreground", children: uiCopy.agentChatPending })) : null, message.attachment ? (_jsxs("div", { className: "mt-2 rounded-md border border-border bg-card p-2", children: [_jsx("p", { className: "mb-1 truncate text-xs font-medium text-muted-foreground", children: message.attachment.fileName }), _jsx(MarkdownView, { content: message.attachment.content })] })) : null, message.actions && message.actions.length > 0 ? (_jsx("div", { className: "mt-3 flex flex-wrap gap-2", children: message.actions.map((action) => {
                                const state = message.actionsState?.[action.id] ?? 'idle';
                                const disabled = state === 'pending' || state === 'done';
                                const label = state === 'done'
                                    ? `${action.label} · ${uiCopy.agentChatActionDone}`
                                    : state === 'pending'
                                        ? uiCopy.agentChatActionPending
                                        : action.label;
                                return (_jsx("button", { type: "button", disabled: disabled, className: cn('inline-flex min-h-11 items-center justify-center rounded-md border border-border bg-background px-3 text-sm font-medium text-foreground transition-colors', 'hover:bg-accent hover:text-accent-foreground', 'disabled:cursor-default disabled:opacity-70', state === 'done' && 'border-primary/40 text-primary', state === 'error' && 'border-destructive text-destructive'), onClick: () => handleActionClick(message.id, action), children: label }, action.id));
                            }) })) : null, message.actionError ? (_jsx("p", { className: "mt-2 text-xs text-destructive", role: "alert", children: message.actionError })) : null] }, message.id)))) }), _jsxs("form", { className: "shrink-0 border-t border-border p-3", onSubmit: onSubmit, children: [_jsxs("div", { className: "rounded-xl border border-border bg-background shadow-sm", children: [_jsx("label", { className: "sr-only", htmlFor: "agent-chat-draft", children: uiCopy.agentChatPlaceholder }), _jsx("textarea", { id: "agent-chat-draft", rows: 3, value: draft, onChange: (event) => setDraft(event.target.value), onKeyDown: onKeyDown, placeholder: uiCopy.agentChatPlaceholder, disabled: sending, className: "w-full resize-none bg-transparent px-3 pt-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none disabled:opacity-70" }), attachment ? (_jsxs("div", { className: "mx-3 mb-2 flex items-center gap-2 rounded-md border border-border bg-muted/50 px-2 py-1.5 text-xs text-foreground", children: [_jsx("span", { className: "min-w-0 flex-1 truncate", children: attachment.fileName }), _jsx("button", { type: "button", className: "inline-flex h-8 w-8 items-center justify-center rounded-md hover:bg-muted", "aria-label": uiCopy.agentChatRemoveAttachment, onClick: () => {
                                            setAttachment(null);
                                            if (fileInputRef.current)
                                                fileInputRef.current.value = '';
                                        }, children: _jsx(X, { className: "h-3.5 w-3.5", "aria-hidden": true }) })] })) : null, _jsxs("div", { className: "flex items-center gap-2 px-2 pb-2", children: [_jsxs("div", { className: "flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden", children: [_jsxs("span", { className: "inline-flex min-h-9 shrink-0 items-center gap-1.5 rounded-full border border-border bg-muted/60 px-2.5 text-xs font-medium text-foreground", children: [_jsx(InfinityIcon, { className: "h-3.5 w-3.5", "aria-hidden": true }), uiCopy.agentChatAgentMode] }), _jsx("span", { className: "truncate text-xs text-muted-foreground", children: modelCaption })] }), _jsx("input", { ref: fileInputRef, id: fileInputId, type: "file", accept: ".md,.markdown,text/markdown", className: "sr-only", onChange: (event) => {
                                            void onPickFile(event);
                                        } }), _jsx("label", { htmlFor: fileInputId, className: "inline-flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground", "aria-label": uiCopy.agentChatAttachMd, title: uiCopy.agentChatAttachMd, children: _jsx(Paperclip, { className: "h-4 w-4", "aria-hidden": true }) }), canSend ? (_jsx("button", { type: "submit", className: "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-opacity hover:opacity-90", "aria-label": uiCopy.agentChatSend, children: _jsx(SendHorizontal, { className: "h-4 w-4", "aria-hidden": true }) })) : (_jsx("button", { type: "button", className: "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-border text-muted-foreground", "aria-label": sending
                                            ? uiCopy.agentChatModelPending
                                            : uiCopy.agentChatVoiceUnavailable, title: sending
                                            ? uiCopy.agentChatModelPending
                                            : uiCopy.agentChatVoiceUnavailable, disabled: true, children: _jsx(Mic, { className: "h-4 w-4", "aria-hidden": true }) }))] })] }), attachError ? (_jsx("p", { className: "mt-2 text-xs text-destructive", role: "alert", children: attachError })) : null, sendError ? (_jsx("p", { className: "mt-2 text-xs text-destructive", role: "alert", children: sendError })) : null] })] }));
}
